import { useState } from 'react';
import { ChevronDown, FolderTree, Pencil } from 'lucide-react';
import type { FilingPlan, FilingPlanPreview } from '../shared/desktop-api';
import { FilingPath } from '../renderer/ContentCentralSetup';
import {
  NamingWindow,
  namingSummary,
  type NamingDocumentType,
  type NamingJob,
} from '../renderer/NamingWindow';
import type { PaigeFilingType } from './paige.global';
import { useJobNaming, type JobNaming } from './use-job-naming';
import './file-naming.css';

/** Where the files go, as the naming window names it: the naming is the job's, not one destination's. */
export const namingDestinationLabel = 'each destination’s folder';

/**
 * The job's file naming in Job setup, Export: CapturePoint's naming row from its Export card. How
 * each document type is named, with a real example per type, and "Change how files are named",
 * which opens CapturePoint's naming window. Every destination that writes files (Microsoft 365,
 * Google Drive, Dropbox, Box, SFTP, the download) names documents this way. "Use this naming" saves
 * at once; only a person who runs jobs may change it. Renders nothing off a Paige job.
 */
export function FileNamingCard({ job }: { job?: NamingJob }) {
  const bridge = window.paigeWorkbench;
  const naming = useJobNaming(bridge?.filingPlan, job?.id);
  if (!bridge?.filingPlan || !job) return null;
  return (
    <NamingCard
      job={withTypes(job, naming.saved?.types ?? [])}
      naming={naming}
      mayChange={bridge.runsJobs !== false}
    />
  );
}

interface NamingCardProps {
  job: NamingJob;
  naming: JobNaming;
  mayChange: boolean;
}

/** The card itself: the naming in use, its examples, and the window it opens. */
function NamingCard({ job, naming, mayChange }: NamingCardProps) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const { plan } = naming;
  const use = async (next: FilingPlan) => {
    await naming.save(next);
    setOpen(false);
    setNote('Naming saved. Every destination names new documents this way.');
  };
  return (
    <div className="setup-map-setting paige-file-naming">
      <FolderTree size={22} aria-hidden="true" />
      <span>
        <strong>File naming</strong>
        <small>{headline(naming.saved?.plan ?? null, plan)}</small>
        {plan && <NamingRows job={job} plan={plan} preview={naming.preview} />}
        <button
          type="button"
          className="secondary-button small paige-file-naming-change"
          disabled={!mayChange || !plan}
          onClick={() => setOpen(true)}
        >
          <Pencil size={14} aria-hidden="true" /> Change how files are named
        </button>
        <small role="status">{naming.error ?? note ?? footLine(mayChange)}</small>
      </span>
      {open && plan && (
        <NamingEditor job={job} plan={plan} onClose={() => setOpen(false)} onUse={use} />
      )}
    </div>
  );
}

/** CapturePoint's naming window, saving at once; its files go to each destination's folder. */
function NamingEditor(props: {
  job: NamingJob;
  plan: FilingPlan;
  onClose: () => void;
  onUse: (plan: FilingPlan) => Promise<void>;
}) {
  return <NamingWindow {...props} destinationLabel={namingDestinationLabel} savesNow />;
}

/** The job's types from its setup; the types Paige last read from it when the setup has none here. */
function withTypes(job: NamingJob, types: PaigeFilingType[]): NamingJob {
  if (job.documentTypes.length > 0 || types.length === 0) return job;
  const documentTypes: NamingDocumentType[] = types.map((type) => ({
    ...type,
    status: 'active',
  }));
  return { ...job, documentTypes };
}

function headline(saved: FilingPlan | null, inUse: FilingPlan | null): string {
  if (!inUse) return 'Reading how documents are named...';
  return saved
    ? 'Folders and names built from each document’s fields'
    : 'The suggested naming: the type and month as folders, the party, number and date as the name';
}

function footLine(mayChange: boolean): string {
  return mayChange
    ? 'Used by every destination that files documents. A name already taken gets -2, -3, ...'
    : 'Only a job manager can change how files are named.';
}

type Example = FilingPlanPreview['examples'][number];

/** The first example as CapturePoint draws it, then every type's naming on request. */
function NamingRows({
  job,
  plan,
  preview,
}: {
  job: NamingJob;
  plan: FilingPlan;
  preview: FilingPlanPreview | null;
}) {
  const [all, setAll] = useState(false);
  const types = job.documentTypes.filter((type) => type.status !== 'archived');
  const examples = (preview?.examples ?? []).filter(
    (example, index, list) =>
      list.findIndex((other) => other.documentType === example.documentType) === index,
  );
  const first = examples[0];
  return (
    <span className="paige-file-naming-rows">
      {first ? (
        <FilingPath folders={first.folders} fileName={`${first.preferredBaseName}.pdf`} />
      ) : (
        <small>The example appears once a document is scanned.</small>
      )}
      {types.length > 0 && (
        <button
          type="button"
          className="quiet-button small"
          aria-expanded={all}
          onClick={() => setAll((value) => !value)}
        >
          {all ? 'Hide the types' : `Show every type (${types.length})`}
          <ChevronDown size={14} aria-hidden="true" className={all ? 'flip' : undefined} />
        </button>
      )}
      {all && <TypeNamings types={types} plan={plan} examples={examples} />}
    </span>
  );
}

/** Each type's naming: its real example when the job has one, else how its names are built. */
function TypeNamings({
  types,
  plan,
  examples,
}: {
  types: NamingDocumentType[];
  plan: FilingPlan;
  examples: Example[];
}) {
  return (
    <span className="cc-paths">
      {types.map((type) => {
        const example = examples.find((item) => item.documentType === type.name);
        const rule = plan.rules.find((item) => item.documentTypeId === type.id);
        return example ? (
          <FilingPath
            key={type.id}
            label={type.name}
            folders={example.folders}
            fileName={`${example.preferredBaseName}.pdf`}
          />
        ) : (
          <small key={type.id} className="paige-file-naming-type">
            <b>{type.name}</b> {rule ? namingSummary(rule, type) : 'The suggested naming'}
          </small>
        );
      })}
    </span>
  );
}
